import React from 'react';
import { ChevronRight, Home, Building2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { getBreadcrumbs } from '@/config/navigation';
import { useCompany } from '@/features/multi-company';

interface BreadcrumbsProps {
  currentPath: string;
  onNavigate: (path: string) => void;
  className?: string;
}

export const Breadcrumbs: React.FC<BreadcrumbsProps> = ({
  currentPath,
  onNavigate,
  className,
}) => {
  const breadcrumbs = getBreadcrumbs(currentPath);
  const { currentCompany } = useCompany();

  if (breadcrumbs.length === 0) {
    return null;
  }

  return (
    <nav
      aria-label="Breadcrumb"
      className={cn('flex items-center space-x-1 text-sm text-muted-foreground', className)}
    >
      {/* Company indicator */}
      {currentCompany && (
        <>
          <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-gradient-to-r from-primary/15 to-primary/10 border-2 border-primary/30 shadow-sm hover:shadow-md transition-all duration-200">
            <Building2 className="h-3.5 w-3.5 text-primary" />
            <span className="font-bold text-primary text-xs">{currentCompany.name}</span>
          </div>
          <ChevronRight className="h-4 w-4 text-primary/50" />
        </>
      )}
      
      <button
        onClick={() => onNavigate('/')}
        className="flex items-center hover:text-foreground transition-colors"
        aria-label="Home"
      >
        <Home className="h-4 w-4" />
      </button>

      {breadcrumbs.map((item, index) => {
        const isLast = index === breadcrumbs.length - 1;
        const Icon = item.icon;

        return (
          <React.Fragment key={item.id}>
            <ChevronRight className="h-4 w-4" />
            <button
              onClick={() => item.path && onNavigate(item.path)}
              disabled={!item.path || isLast}
              className={cn(
                'flex items-center gap-1.5 transition-colors',
                isLast
                  ? 'text-foreground font-medium cursor-default'
                  : 'hover:text-foreground cursor-pointer',
                !item.path && 'cursor-default'
              )}
            >
              {Icon && <Icon className="h-4 w-4" />}
              <span>{item.label}</span>
            </button>
          </React.Fragment>
        );
      })}
    </nav>
  );
};
